import { Status } from '@/Enums/Enums';
import type { DVGItem } from '@/Typings';
import type { PropertyRow, SubContextData } from '@/Datastructures';
import type {
    Dictionary,
    ControlViewModel,
    LabelViewModel as LabelViewModelT,
    TextBoxViewModel as TextBoxViewModelT,
    CheckBoxViewModel as CheckBoxViewModelT,
    ExpanderBoxViewModel as ExpanderBoxViewModelT,
    DropDownListViewModel as DropDownListViewModelT,
    ResourceManager,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    Utilities,
    ImageHandler,
    ComponentManager,
    LocatorHelper,
    GlobalConfiguration,
} from 'radpresentation.htmlclient.defaultviewextensions';
import type PropertyTabViewModel from '@/ViewModels/Controls/PropertyTabViewModel';
import type AssociationTabViewModel from '@/ViewModels/Controls/AssociationTabViewModel';
import type ExtensionEditorViewModel from '@/ViewModels/Controls/ExtensionEditorViewModel';
import MemberTabViewModel from '@/ViewModels/Controls/MemberTabViewModel';

class EditMaskTabViewModel extends MemberTabViewModel {
    protected LabelViewModel =
        ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    protected TextBoxViewModel =
        ComponentManager.getViewModel<typeof TextBoxViewModelT>(
            'TextBoxViewModel'
        );
    protected CheckBoxViewModel =
        ComponentManager.getViewModel<typeof CheckBoxViewModelT>(
            'CheckBoxViewModel'
        );
    protected DropDownListViewModel = ComponentManager.getViewModel<
        typeof DropDownListViewModelT
    >('DropDownListViewModel');
    protected ExpanderBoxViewModel = ComponentManager.getViewModel<
        typeof ExpanderBoxViewModelT
    >('ExpanderBoxViewModel');

    // dv
    private DetailViewExpander: ExpanderBoxViewModelT;
    public VisibleInEditMode: CheckBoxViewModelT;
    private UIGeneratorLabelDV: LabelViewModelT;
    private UIGeneratorDV: DropDownListViewModelT;

    private ParameterLabelDV: LabelViewModelT;
    private ParameterDV: TextBoxViewModelT;

    private labelSubItem: LabelViewModelT;
    private labelSubItemType: LabelViewModelT;
    private labelSubItemGuiGenerator: LabelViewModelT;

    // dv - MLS
    private dvgItemCreated?: DVGItem;
    private dvgItemSeparatorString?: DVGItem;
    private dvgItemForEachLanguageEqual?: DVGItem;

    private uiGeneratorsMLString: Array<string>;

    // sv
    private SummaryViewExpander: ExpanderBoxViewModelT;
    private VisibleInSummary: CheckBoxViewModelT;
    private UIGeneratorLabelSV: LabelViewModelT;
    private UIGeneratorSV: DropDownListViewModelT;
    private ParameterLabelSV: LabelViewModelT;
    private ParameterSV: TextBoxViewModelT;

    protected relatedData: PropertyRow;
    private fieldsWithDynamicEnabledState: Array<ControlViewModel>;

    // injected
    public isInUpdateMode = false;
    public DVGItem: DVGItem = null;
    public context: ExtensionEditorViewModel;
    public propertyTabViewModel: PropertyTabViewModel;
    public associationTabViewModel: AssociationTabViewModel;

    protected subscriptions: Array<KnockoutSubscription> = [];
    protected subContextSubscriptions: Array<KnockoutSubscription> = [];

    private subContextNodes: Record<string, Array<SubContextData>> = null;
    private subContextNode: KnockoutObservableArray<SubContextData>;

    public constructor(protected locator: KnockoutObservable<string>) {
        super(locator);
        this.subContextNode = ko.observableArray();
        this.createDvExpander();
        this.createSvExpander();

        this.setUpEnabledStatesConfiguration();
        this.syncFocusableWithEnabledStates();

        this.uiGeneratorsMLString = [
            'MLStringBox',
            'MLStringRichInputBox',
            'MLStringPopupBox',
            'MLStringLabel',
        ];

        this.labelSubItem = this.createLabel(
            'RADEM.RADEXTENSIONMANAGER.SUBITEM'
        );
        this.labelSubItemType = this.createLabel(
            'RADEM.RADEXTENSIONMANAGER.SUBITEM_TYPE'
        );
        this.labelSubItemGuiGenerator = this.createLabel(
            'RADEM.RADEXTENSIONMANAGER.SUBITEM_GUI_GENERATOR'
        );
    }

    public updateContent(relatedData?: PropertyRow) {
        this.isInUpdateMode = true;

        if (!relatedData) {
            relatedData = {} as PropertyRow;
        }

        this.relatedData = relatedData;

        this.updateDetailViewTypeDropDown();
        this.updateSummaryViewTypeDropDown();

        this.ParameterDV.value(relatedData.UIGeneratorDvParameter || '');
        this.ParameterSV.value(relatedData.UIGeneratorSvParameter || '');

        if (this.subContextNode()?.length > 0) {
            this.subContextNode()
                ?.filter(x => x.childNode === 'Value')[0]
                ?.dropDown.value(
                    relatedData.ChildNodes?.filter(
                        node =>
                            node.Member?.Name === 'Value' ||
                            node.Name === 'Value'
                    )[0]?.UIGenerator.split('?')[0] || ''
                );

            this.subContextNode()
                ?.filter(x => x.childNode === 'ValueMLS')[0]
                ?.dropDown.value(
                    relatedData.ChildNodes?.filter(
                        node =>
                            node.Member?.Name === 'ValueMLS' ||
                            node.Name === 'ValueMLS'
                    )[0]?.UIGenerator || ''
                );
        }

        // update isEnabled states
        let enabledState = !_.isEmpty(relatedData);
        this.fieldsWithDynamicEnabledState.forEach(field =>
            field.isEnabled(enabledState)
        );
        this.VisibleInEditMode.isEnabled(
            enabledState &&
                ((Utilities.isNullOrUndefined(relatedData.Cardinality) &&
                    !this.propertyTabViewModel.Required.isChecked()) ||
                    (!Utilities.isNullOrUndefined(relatedData.Cardinality) &&
                        !this.associationTabViewModel.Mandatory.isChecked()) ||
                    (!Utilities.isNullOrUndefined(relatedData.Cardinality) &&
                        !Utilities.isNullOrUndefinedOrBlank(
                            this.associationTabViewModel.MandatoryWhen.value()
                        )))
        );

        // update isChecked states
        this.VisibleInSummary.isChecked(
            relatedData.IsVisibleOnSummaryView == true
        );
        this.VisibleInEditMode.isChecked(
            relatedData.IsVisible || relatedData.Required
        );

        this.isInUpdateMode = false;
    }

    private updateDetailViewTypeDropDown() {
        let typ = this.relatedData.Typ;
        let uiGenerator = this.relatedData.UIGenerator;

        let dvDataUpdate = this.createUIGeneratorDVOptionsByType(typ);
        let hasMatchInDvDropDownEntries = dvDataUpdate.some(
            option => option.name == uiGenerator
        );
        let defaultDVSelection = this.uiGeneratorDvDefaultsByType[typ] || '';

        this.disposeMlsSubscriptions();
        if (typ === 'Multilingual String') this.handleMultilingualString();

        if (_.isEmpty(dvDataUpdate) || !hasMatchInDvDropDownEntries) {
            this.UIGeneratorDV.data([
                this.createOption(uiGenerator, uiGenerator, 'control.png'),
            ]);
            this.UIGeneratorDV.value(uiGenerator);
        } else {
            this.UIGeneratorDV.data(dvDataUpdate);
            this.UIGeneratorDV.value(
                hasMatchInDvDropDownEntries ? uiGenerator : defaultDVSelection
            );
        }
    }

    private updateSummaryViewTypeDropDown() {
        let typ = this.relatedData.Typ;
        let uiGeneratorSv = this.relatedData.UIGeneratorSummaryView;

        let svDataUpdate = this.createUIGeneratorSVOptionsByType(typ);
        let hasMatchInSvDropDownEntries = svDataUpdate.some(
            option => option.name == uiGeneratorSv
        );
        let defaultSVSelection = this.uiGeneratorSvDefaultsByType[typ] || '';

        if (_.isEmpty(svDataUpdate) || !hasMatchInSvDropDownEntries) {
            let icon = Utilities.endsWith(uiGeneratorSv, 'Label')
                ? 'label.png'
                : 'control.png';
            this.UIGeneratorSV.data([
                this.createOption(uiGeneratorSv, uiGeneratorSv, icon),
            ]);
            this.UIGeneratorSV.value(uiGeneratorSv);
        } else {
            this.UIGeneratorSV.data(svDataUpdate);
            this.UIGeneratorSV.value(
                hasMatchInSvDropDownEntries ? uiGeneratorSv : defaultSVSelection
            );
        }
    }

    private setUpEnabledStatesConfiguration() {
        this.fieldsWithDynamicEnabledState = [
            this.UIGeneratorDV,
            this.UIGeneratorSV,
            this.ParameterDV,
            this.ParameterSV,
            this.VisibleInSummary,
        ];
    }

    private syncFocusableWithEnabledStates() {
        this.VisibleInEditMode.syncFocusableWithEnabledState();

        this.fieldsWithDynamicEnabledState.forEach(field =>
            field.syncFocusableWithEnabledState()
        );
    }

    public updateUiGeneratorOptionsByType() {
        let typ = this.relatedData.Typ;

        let dvUpdate = this.createUIGeneratorDVOptionsByType(typ);
        let svUpdate = this.createUIGeneratorSVOptionsByType(typ);

        this.UIGeneratorDV.data(dvUpdate);
        this.UIGeneratorSV.data(svUpdate);

        const uiGenerator = this.uiGeneratorDvSubContextDefaultsByType[typ];
        if (uiGenerator) {
            let data = this.subContextNode();
            data?.filter(x => x.childNode === 'Value')[0]?.dropDown.value(
                uiGenerator['Value']
            );
            data?.filter(x => x.childNode === 'ValueMLS')[0]?.dropDown.value(
                uiGenerator['ValueMLS']
            );
        }
    }

    public selectDefaultUiGenerators() {
        let typ = this.relatedData.Typ;
        let dvUpdate: string = this.uiGeneratorDvDefaultsByType[typ];
        let svUpdate: string = this.uiGeneratorSvDefaultsByType[typ];

        if (dvUpdate.indexOf('?') > -1) {
            const parts = dvUpdate.split('?');

            this.UIGeneratorDV.value(parts[0]);
            this.ParameterDV.value(parts[1]);
        } else {
            this.UIGeneratorDV.value(dvUpdate);
            this.ParameterDV.value('');
        }

        if (svUpdate.indexOf('?') > -1) {
            const parts = svUpdate.split('?');

            this.UIGeneratorSV.value(parts[0]);
            this.ParameterSV.value(parts[1]);
        } else {
            this.UIGeneratorSV.value(svUpdate);
            this.ParameterSV.value('');
        }
    }

    private createDvExpander() {
        this.DetailViewExpander = this.createExpander(
            'RADEM.RADEXTENSIONMANAGER.DETAILVIEWLABEL'
        );

        // visible in dv
        this.VisibleInEditMode = this.createCheckbox(
            this.createLabel('RADEM.RADEXTENSIONMANAGER.DISPLAYONDETAILVIEW'),
            LocatorHelper.buildLocator(this.locator, 'visible_dv')
        );
        const isVisibleInEditModeSubscription =
            this.VisibleInEditMode.isChecked.subscribe(newValue => {
                if (this.isInUpdateMode) {
                    return;
                }

                this.DVGItem.IsVisible = newValue;
                this.relatedData.IsVisible = newValue;

                Status.setChanged(this.DVGItem);
            });
        this.subscriptions.push(isVisibleInEditModeSubscription);

        // ui-generator
        this.UIGeneratorLabelDV = this.createLabel(
            'RADEM.RADEXTENSIONMANAGER.UIGENERATOR'
        );
        this.UIGeneratorDV = this.createTypePicker(
            LocatorHelper.buildLocator(this.locator, 'uigenerator_dv')
        );

        const uiGeneratorDVSubscription = this.UIGeneratorDV.selected.subscribe(
            (newOption: TypeOption) => {
                if (this.isInUpdateMode) {
                    return;
                }

                let defaultOption = this.getCurrentDefaultOption('dv');
                let hasParameter = Utilities.contains(
                    this.DVGItem.UIGenerator,
                    '?'
                );
                let parameterPart = hasParameter
                    ? this.DVGItem.UIGenerator.split('?')[1]
                    : '';
                let uiGenerator = newOption
                    ? newOption.name
                    : defaultOption['name'];
                let uiGeneratorWithParam = Utilities.isNotBlank(parameterPart)
                    ? uiGenerator + '?' + parameterPart
                    : uiGenerator;

                this.DVGItem.UIGenerator = uiGeneratorWithParam;
                this.relatedData.UIGenerator = uiGenerator;

                this.context.refreshPropertiesGrid();
                Status.setChanged(this.DVGItem);
            }
        );
        this.subscriptions.push(uiGeneratorDVSubscription);

        this.subContextNodes = {
            'Multilingual String': [
                {
                    childNode: 'Value',
                    type: 'MLString.Value',
                    dropDown: undefined,
                },
                { childNode: 'ValueMLS', type: 'string', dropDown: undefined },
            ],
        };

        // ui-generator parameter
        this.ParameterLabelDV = this.createLabel(
            'RADEM.RADEXTENSIONMANAGER.UIGENERATORPARAMETER'
        );
        this.ParameterDV = new this.TextBoxViewModel({
            isEnabled: false,
            locator: LocatorHelper.buildLocator(this.locator, 'param_dv'),
        });

        let uiGeneratorParamDVSubscription = this.ParameterDV.value.subscribe(
            newValue => {
                if (this.isInUpdateMode) {
                    return;
                }

                if (
                    this.relatedData.DotNetTypeName ===
                    'RAD.RADFoundation.ProblemDomain.MLString'
                ) {
                    let valueNode = this.relatedData.ChildNodes.filter(
                        child =>
                            child.Member?.Name === 'Value' ||
                            child.Name === 'Value'
                    )[0];
                    let hasParameter = valueNode.UIGenerator.indexOf('?') > -1;
                    let basePart = hasParameter
                        ? valueNode.UIGenerator.split('?')[0]
                        : valueNode.UIGenerator;

                    valueNode.UIGenerator =
                        basePart +
                        (Utilities.isBlank(newValue) ? '' : '?' + newValue);
                    this.relatedData.UIGeneratorDvParameter = newValue;

                    this.createOrUpdateChildNodesForMLS(valueNode);
                } else {
                    let hasParameter =
                        this.DVGItem.UIGenerator.indexOf('?') > -1;
                    let basePart = hasParameter
                        ? this.DVGItem.UIGenerator.split('?')[0]
                        : this.DVGItem.UIGenerator;

                    this.DVGItem.UIGenerator =
                        basePart +
                        (Utilities.isBlank(newValue) ? '' : '?' + newValue);
                    this.relatedData.UIGeneratorDvParameter = newValue;

                    this.context.refreshPropertiesGrid();
                }

                Status.setChanged(this.DVGItem);
            }
        );
        this.subscriptions.push(uiGeneratorParamDVSubscription);
    }

    private createOrUpdateChildNodesForMLS(dvgItem: DVGItem) {
        let filtered = this.relatedData.ChildNodes.filter(node => {
            if (node.Member) return node.Member.Name === dvgItem.Member.Name;

            return node.Name === dvgItem.Name;
        });

        if (filtered.length === 1) {
            let elementPos = this.relatedData.ChildNodes.map(node => {
                if (node.Member) return node.Member.Name;

                return node.Name;
            }).indexOf(dvgItem.Member ? dvgItem.Member.Name : dvgItem.Name);

            if (elementPos > -1) {
                Status.setChanged(this.relatedData.ChildNodes[elementPos]);
                this.relatedData.ChildNodes[elementPos].UIGenerator =
                    dvgItem.UIGenerator;
            }
        } else {
            this.relatedData.ChildNodes.push(dvgItem);
        }
    }

    private handleMultilingualString(): void {
        this.disposeMlsSubscriptions();
        this.subContextNode([]);
        const dropDownValue = this.createTypePicker(
            LocatorHelper.buildLocator(this.locator, 'subcontext_value')
        );
        const dropDownValueMLS = this.createTypePicker(
            LocatorHelper.buildLocator(this.locator, 'subcontext_value_mls')
        );

        let subContext =
            this.createUIGeneratorDVOptionsByType('SubContext_Value');
        let subContextMLS = this.createUIGeneratorDVOptionsByType(
            'SubContext_ValueMLS'
        );

        dropDownValue.data(subContext);
        dropDownValueMLS.data(subContextMLS);

        this.fieldsWithDynamicEnabledState.push(dropDownValue);
        this.fieldsWithDynamicEnabledState.push(dropDownValueMLS);

        const uiSubItemGeneratorValue = dropDownValue.selected.subscribe(
            (newOption: TypeOption) => {
                if (this.isInUpdateMode) return;

                if (
                    !this.dvgItemCreated ||
                    !this.dvgItemForEachLanguageEqual ||
                    !this.dvgItemSeparatorString
                )
                    this.initMLSBaseTypes();

                let existingDvgItem = this.relatedData.ChildNodes.filter(
                    childNode => {
                        if (childNode.Member)
                            return childNode.Member.Name === 'Value';

                        return null;
                    }
                );

                if (existingDvgItem.length == 0) {
                    let subContextValue = {} as DVGItem;
                    subContextValue.UIGenerator = newOption.name;
                    subContextValue.Status = Status.New;
                    subContextValue.Name = 'Value';
                    subContextValue.IsVisible = true;

                    this.createOrUpdateChildNodesForMLS(subContextValue);
                } else {
                    let hasParameter =
                        existingDvgItem[0].UIGenerator.indexOf('?') > 0;
                    let uiGeneratorWithParameter =
                        existingDvgItem[0].UIGenerator.split('?');
                    existingDvgItem[0].UIGenerator = `${newOption.name}${hasParameter ? '?' + uiGeneratorWithParameter[1] : ''}`;

                    this.createOrUpdateChildNodesForMLS(existingDvgItem[0]);
                }

                Status.setChanged(this.DVGItem);
            }
        );

        this.subContextSubscriptions.push(uiSubItemGeneratorValue);

        const uiSubItemGeneratorValueMLS = dropDownValueMLS.selected.subscribe(
            (newOption: TypeOption) => {
                if (this.isInUpdateMode) return;

                if (
                    !this.dvgItemCreated ||
                    !this.dvgItemForEachLanguageEqual ||
                    !this.dvgItemSeparatorString
                )
                    this.initMLSBaseTypes();

                let existingDvgItem = this.relatedData.ChildNodes.filter(
                    childNode => {
                        if (childNode.Member)
                            return childNode.Member.Name === 'Value';

                        return null;
                    }
                );

                if (existingDvgItem.length == 0) {
                    let subContextValueMLS = {} as DVGItem;
                    subContextValueMLS.UIGenerator = newOption.name;
                    subContextValueMLS.Status = Status.New;
                    subContextValueMLS.Name = 'ValueMLS';
                    subContextValueMLS.IsVisible = false;

                    this.createOrUpdateChildNodesForMLS(subContextValueMLS);
                } else {
                    existingDvgItem[0].UIGenerator = newOption.name;
                    this.createOrUpdateChildNodesForMLS(existingDvgItem[0]);
                }

                Status.setChanged(this.DVGItem);
            }
        );

        this.subContextSubscriptions.push(uiSubItemGeneratorValueMLS);

        let data = this.subContextNodes['Multilingual String'];
        data.filter(x => x.childNode === 'Value')[0].dropDown = dropDownValue;
        data.filter(x => x.childNode === 'ValueMLS')[0].dropDown =
            dropDownValueMLS;
        this.subContextNode(data);
    }

    private initMLSBaseTypes(): void {
        if (!this.DVGItem.ChildNodes) this.DVGItem.ChildNodes = [];

        let dvgItemMLS = {} as DVGItem;
        dvgItemMLS.UIGenerator = 'CheckBox';
        dvgItemMLS.Status = Status.New;
        dvgItemMLS.Name = 'Created';
        dvgItemMLS.IsVisible = false;

        if (
            !this.DVGItem.ChildNodes.some(
                node => node.Member?.Name == 'Created'
            )
        )
            this.DVGItem.ChildNodes.push(dvgItemMLS);

        this.dvgItemCreated = dvgItemMLS;

        dvgItemMLS = {} as DVGItem;
        dvgItemMLS.UIGenerator = 'CheckBox';
        dvgItemMLS.Status = Status.New;
        dvgItemMLS.Name = 'ForEachLanguageEquals';
        dvgItemMLS.IsVisible = false;

        if (
            !this.DVGItem.ChildNodes.some(
                node => node.Member?.Name == 'ForEachLanguageEquals'
            )
        )
            this.DVGItem.ChildNodes.push(dvgItemMLS);

        this.dvgItemForEachLanguageEqual = dvgItemMLS;

        dvgItemMLS = {} as DVGItem;
        dvgItemMLS.UIGenerator = 'TextBox';
        dvgItemMLS.Status = Status.New;
        dvgItemMLS.Name = 'SeparatorString';
        dvgItemMLS.IsVisible = false;

        if (
            !this.DVGItem.ChildNodes.some(
                node => node.Member?.Name == 'SeparatorString'
            )
        )
            this.DVGItem.ChildNodes.push(dvgItemMLS);

        this.dvgItemSeparatorString = dvgItemMLS;
    }

    private createSvExpander() {
        this.SummaryViewExpander = this.createExpander(
            'RADEM.RADEXTENSIONMANAGER.SUMMARYVIEW'
        );

        // visible in sv
        this.VisibleInSummary = this.createCheckbox(
            this.createLabel('RADEM.RADEXTENSIONMANAGER.DISPLAYONSUMMARYVIEW'),
            LocatorHelper.buildLocator(this.locator, 'visible_sv')
        );
        let isVisibleInSummarySubscription =
            this.VisibleInSummary.isChecked.subscribe(newValue => {
                if (this.isInUpdateMode) {
                    return;
                }

                this.DVGItem.IsVisibleOnSummaryView = newValue;
                this.relatedData.IsVisibleOnSummaryView = newValue;

                Status.setChanged(this.DVGItem);
            });
        this.subscriptions.push(isVisibleInSummarySubscription);

        // ui-generator
        this.UIGeneratorLabelSV = this.createLabel(
            'RADEM.RADEXTENSIONMANAGER.UIGENERATOR'
        );
        this.UIGeneratorSV = this.createTypePicker(
            LocatorHelper.buildLocator(this.locator, 'uigenerator_sv')
        );

        let uiGeneratorSVSubscription = this.UIGeneratorSV.selected.subscribe(
            (newOption: TypeOption) => {
                if (this.isInUpdateMode) {
                    return;
                }

                let defaultOption = this.getCurrentDefaultOption('sv');
                let hasParameter = Utilities.contains(
                    this.DVGItem.UIGeneratorSummaryView,
                    '?'
                );
                let parameterPart = hasParameter
                    ? this.DVGItem.UIGeneratorSummaryView.split('?')[1]
                    : '';
                let uiGenerator = newOption
                    ? newOption.name
                    : defaultOption['name'];
                let uiGeneratorWithParam = Utilities.isNotBlank(parameterPart)
                    ? uiGenerator + '?' + parameterPart
                    : uiGenerator;

                this.DVGItem.UIGeneratorSummaryView = uiGeneratorWithParam;
                this.relatedData.UIGeneratorSummaryView = uiGenerator;

                Status.setChanged(this.DVGItem);
            }
        );
        this.subscriptions.push(uiGeneratorSVSubscription);

        // ui-generator parameter
        this.ParameterLabelSV = this.createLabel(
            'RADEM.RADEXTENSIONMANAGER.UIGENERATORPARAMETER'
        );
        this.ParameterSV = new this.TextBoxViewModel({
            isEnabled: false,
            locator: LocatorHelper.buildLocator(this.locator, 'param_sv'),
        });
        let uiGeneratorParamDVSubscription = this.ParameterSV.value.subscribe(
            newValue => {
                if (this.isInUpdateMode) {
                    return;
                }

                let hasParameter =
                    this.DVGItem.UIGeneratorSummaryView.indexOf('?') > -1;
                let basePart = hasParameter
                    ? this.DVGItem.UIGeneratorSummaryView.split('?')[0]
                    : this.DVGItem.UIGeneratorSummaryView;

                this.DVGItem.UIGeneratorSummaryView =
                    basePart +
                    (Utilities.isBlank(newValue) ? '' : '?' + newValue);
                this.relatedData.UIGeneratorSvParameter = newValue;

                Status.setChanged(this.DVGItem);
            }
        );
        this.subscriptions.push(uiGeneratorParamDVSubscription);
    }

    private getCurrentDefaultOption(dvOrSv: 'dv' | 'sv'): TypeOption {
        let dv = dvOrSv == 'dv';
        let typ = this.relatedData.Typ;
        let currentDefaultName = (
            dv
                ? this.uiGeneratorDvDefaultsByType
                : this.uiGeneratorSvDefaultsByType
        )[typ];
        let data = dv ? this.UIGeneratorDV.data() : this.UIGeneratorSV.data();

        return Utilities.findFirst(
            data,
            anyOption => anyOption.name == currentDefaultName
        );
    }

    private createTypePicker(locator: ObservableOrDefault<string>) {
        return new this.DropDownListViewModel({
            data: [],
            dataTextField: 'caption',
            dataValueField: 'name',
            template: iconUrlTemplate,
            valueTemplate: iconUrlTemplate,
            isEnabled: false,
            locator: locator,
        });
    }

    private createUIGeneratorDVOptionsByType(
        dotnetTypeName: string
    ): Array<TypeOption> {
        let config = this.uiGeneratorDVOptionsByType[dotnetTypeName] || [];

        return config.map(option =>
            this.createOption(option.caption, option.name, option.icon)
        );
    }

    private createUIGeneratorSVOptionsByType(
        dotnetTypeName: string
    ): Array<TypeOption> {
        let config = this.uiGeneratorSVOptionsByType[dotnetTypeName] || [];

        return config.map(option =>
            this.createOption(option.caption, option.name, option.icon)
        );
    }

    private uiGeneratorDvDefaultsByType = {
        boolean: 'CheckBox',
        Currency: 'CurrencyTextBox',
        CurrencyT: 'CurrencyTextBox',
        DateTime: 'DatePicker',
        'Embedded Medium': 'EmbeddedMediumBox',
        'Embedded File Link': 'EmbeddedMediumBox',
        'Nullable<DateTime>': 'DatePicker',
        float: 'NumericTextBox',
        int: 'NumericTextBox?FloatingPrecision=0',
        'Multilingual String': 'DataTypeEmptyGroupBox',
        String: 'TextBox',
    };

    // Default-Values for Subcontext-Types
    private uiGeneratorDvSubContextDefaultsByType = {
        'Multilingual String': {
            Value: 'MLStringBox',
            ValueMLS: 'TextBox',
        },
    };

    private uiGeneratorSvDefaultsByType = {
        boolean: 'CheckBoxLabel',
        Currency: 'CurrencyLabel',
        CurrencyT: 'CurrencyLabel',
        DateTime: 'DateTimeLabel',
        'Embedded Medium': 'EmbeddedMediumLink',
        'Embedded File Link': 'EmbeddedMediumLink',
        'Nullable<DateTime>': 'DateTimeLabel',
        float: 'Label',
        int: 'Label',
        'Multilingual String': 'MLStringLabel',
        String: 'Label',
    };

    // Zusammenfassungsmaske
    private uiGeneratorSVOptionsByType = {
        boolean: [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' },
            {
                caption: 'CheckBoxLabel',
                name: 'CheckBoxLabel',
                icon: 'label.png',
            }, // sv default
            { caption: 'CheckBox', name: 'CheckBox', icon: 'checkbox.png' }, // dv default
            { caption: 'Label', name: 'Label', icon: 'label.png' },
        ],
        Currency: [
            {
                caption: 'CurrencyTextBox',
                name: 'CurrencyTextBox',
                icon: 'currency.png',
            }, // dv default
            {
                caption: 'CurrencyLabel',
                name: 'CurrencyLabel',
                icon: 'label.png',
            }, // sv default
        ],
        CurrencyT: [
            {
                caption: 'CurrencyTextBox',
                name: 'CurrencyTextBox',
                icon: 'currency.png',
            }, // dv default
            {
                caption: 'CurrencyLabel',
                name: 'CurrencyLabel',
                icon: 'label.png',
            }, // sv default
        ],
        'Embedded Medium': [
            {
                caption: 'EmbeddedMediumBox',
                name: 'EmbeddedMediumBox',
                icon: 'embedded_mediumbox.png',
            }, // dv default
            {
                caption: 'EmbeddedMediumLink',
                name: 'EmbeddedMediumLink',
                icon: 'link.png',
            }, // sv default
        ],
        'Embedded File Link': [
            {
                caption: 'EmbeddedMediumBox',
                name: 'EmbeddedMediumBox',
                icon: 'embedded_mediumbox.png',
            }, // dv default
            {
                caption: 'EmbeddedMediumLink',
                name: 'EmbeddedMediumLink',
                icon: 'link.png',
            }, // sv default
        ],
        DateTime: [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' },
            {
                caption: 'DatePicker',
                name: 'DatePicker',
                icon: GlobalConfiguration.inlineImages.DATEPICKER,
            }, // dv default
            {
                caption: 'DateAndTimePicker',
                name: 'DateAndTimePicker',
                icon: 'datetimepicker.png',
            },
            {
                caption: 'TimePicker',
                name: 'TimePicker',
                icon: 'timepicker.png',
            },
            { caption: 'Calendar', name: 'Calendar', icon: 'calendar.png' },
            {
                caption: 'DateTimeLabel',
                name: 'DateTimeLabel',
                icon: 'label.png',
            }, // sv default
            { caption: 'Label', name: 'Label', icon: 'label.png' },
        ],
        'Nullable<DateTime>': [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' },
            {
                caption: 'DatePicker',
                name: 'DatePicker',
                icon: GlobalConfiguration.inlineImages.DATEPICKER,
            }, // dv default
            {
                caption: 'DateAndTimePicker',
                name: 'DateAndTimePicker',
                icon: 'datetimepicker.png',
            },
            {
                caption: 'TimePicker',
                name: 'TimePicker',
                icon: 'timepicker.png',
            },
            { caption: 'Calendar', name: 'Calendar', icon: 'calendar.png' },
            {
                caption: 'DateTimeLabel',
                name: 'DateTimeLabel',
                icon: 'label.png',
            }, // sv default
            { caption: 'Label', name: 'Label', icon: 'label.png' },
        ],
        float: [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' }, // dv default
            {
                caption: 'NumericTextBox',
                name: 'NumericTextBox',
                icon: 'numericbox.png',
            },
            { caption: 'Label', name: 'Label', icon: 'label.png' }, // sv default
        ],
        int: [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' }, // dv default
            {
                caption: 'NumericTextBox',
                name: 'NumericTextBox',
                icon: 'numericbox.png',
            },
            { caption: 'Label', name: 'Label', icon: 'label.png' }, // sv default
        ],
        'Multilingual String': [
            {
                caption: 'MLStringLabel',
                name: 'MLStringLabel',
                icon: 'label.png',
            }, // sv default
        ],
        String: [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' }, // dv default
            {
                caption: 'MultilineTextBox',
                name: 'MultilineTextBox',
                icon: 'textarea.png',
            },
            {
                caption: 'PasswordBox',
                name: 'PasswordBox',
                icon: 'passwordbox.png',
            },
            { caption: 'UrlLink', name: 'UrlLink', icon: 'link.png' },
            {
                caption: 'ColorPicker',
                name: 'ColorPicker',
                icon: 'colorpicker.png',
            },
            { caption: 'Label', name: 'Label', icon: 'label.png' }, // sv default
        ],
        Serial: [
            {
                caption: 'DataTypeEmptyGroupBox',
                name: 'DataTypeEmptyGroupBox',
                icon: 'group_box.png',
            }, // dv default
        ],
        ToOneAssociation: [
            {
                caption: 'AssoziationLabel',
                name: 'AssoziationLabel',
                icon: 'label.png',
            },
        ],
        ToManyAssociation: [
            {
                caption: 'AssoziationLabel',
                name: 'AssoziationLabel',
                icon: 'label.png',
            },
        ],
    } as { [key: string]: Array<TypeOptionParameter> };

    // Editiermaske
    private uiGeneratorDVOptionsByType = (() => {
        let copy = JSON.parse(
            JSON.stringify(this.uiGeneratorSVOptionsByType)
        ) as { [key: string]: Array<TypeOptionParameter> };
        let index = _.findIndex(
            copy['boolean'],
            option => option['caption'] == 'CheckBoxLabel'
        );
        copy['boolean'].splice(index, 1);

        let associationOptions = [
            {
                caption: 'AssociationBox',
                name: 'AssociationBox',
                icon: 'control.png',
            },
            {
                caption: 'AssociationCheckBox',
                name: 'AssociationCheckBox',
                icon: 'control.png',
            },
            {
                caption: 'AssociationGrid',
                name: 'AssociationGrid',
                icon: 'control.png',
            },
            {
                caption: 'AssociationLabel',
                name: 'AssociationLabel',
                icon: 'control.png',
            },
            {
                caption: 'AssociationLink',
                name: 'AssociationLink',
                icon: 'control.png',
            },
            {
                caption: 'AssociationMultiAutocompleteBox',
                name: 'AssociationMultiAutocompleteBox',
                icon: 'control.png',
            },
            {
                caption: 'AssociationMultiDropDown',
                name: 'AssociationMultiDropDown',
                icon: 'control.png',
            },
            {
                caption: 'AssociationMultiLabel',
                name: 'AssociationMultiLabel',
                icon: 'control.png',
            },
            {
                caption: 'AssociationSingleAutocompleteBox',
                name: 'AssociationSingleAutocompleteBox',
                icon: 'control.png',
            },
            {
                caption: 'AssociationSingleDropDown',
                name: 'AssociationSingleDropDown',
                icon: 'control.png',
            },
            {
                caption: 'AssociationSupportTable',
                name: 'AssociationSupportTable',
                icon: 'control.png',
            },
            {
                caption: 'AssociationTreeView',
                name: 'AssociationTreeView',
                icon: 'control.png',
            },
            {
                caption: 'AssoziationBox',
                name: 'AssoziationBox',
                icon: 'control.png',
            },
            {
                caption: 'AssoziationCheckBox',
                name: 'AssoziationCheckBox',
                icon: 'control.png',
            },
            {
                caption: 'AssoziationDropDown',
                name: 'AssoziationDropDown',
                icon: 'control.png',
            },
            {
                caption: 'AssoziationGrid',
                name: 'AssoziationGrid',
                icon: 'control.png',
            },
            {
                caption: 'AssoziationLabel',
                name: 'AssoziationLabel',
                icon: 'control.png',
            },
            {
                caption: 'AssoziationLink',
                name: 'AssoziationLink',
                icon: 'control.png',
            },
            {
                caption: 'AssoziationMultiLabel',
                name: 'AssoziationMultiLabel',
                icon: 'control.png',
            },
        ];
        copy['ToOneAssociation'] = associationOptions;
        copy['ToManyAssociation'] = associationOptions;

        copy['Multilingual String'] = [
            {
                caption: 'DataTypeEmptyGroupBox',
                name: 'DataTypeEmptyGroupBox',
                icon: 'group_box.png',
            },
        ];
        copy['String'] = [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' },
            {
                caption: 'MultilineTextBox',
                name: 'MultilineTextBox',
                icon: 'textarea.png',
            },
            {
                caption: 'RichTextBox',
                name: 'RichTextBox',
                icon: 'richtextbox.png',
            },
            {
                caption: 'PasswordBox',
                name: 'PasswordBox',
                icon: 'passwordbox.png',
            },
            { caption: 'UrlLink', name: 'UrlLink', icon: 'link.png' },
            {
                caption: 'ColorPicker',
                name: 'ColorPicker',
                icon: 'colorpicker.png',
            },
            { caption: 'Label', name: 'Label', icon: 'label.png' },
        ];

        // UI-Generators for MLString Sub-Items (equal to Specifier)
        copy['SubContext_Value'] = [
            {
                caption: 'MLStringBox',
                name: 'MLStringBox',
                icon: 'mlstring_box.png',
            },
            {
                caption: 'MLStringRichInputBox',
                name: 'MLStringRichInputBox',
                icon: 'richtextbox.png',
            },
            {
                caption: 'MLStringPopupBox',
                name: 'MLStringPopupBox',
                icon: 'mlstring_popup.png',
            },
            {
                caption: 'MLStringLabel',
                name: 'MLStringLabel',
                icon: 'label.png',
            },
        ];

        // UI-Generators for MLString Sub-Items (equal to Specifier)
        copy['SubContext_ValueMLS'] = [
            { caption: 'TextBox', name: 'TextBox', icon: 'textbox.png' },
            {
                caption: 'MultilineTextBox',
                name: 'MultilineTextBox',
                icon: 'textarea.png',
            },
            {
                caption: 'RichTextBox',
                name: 'RichTextBox',
                icon: 'richtextbox.png',
            },
            {
                caption: 'PasswordBox',
                name: 'PasswordBox',
                icon: 'passwordbox.png',
            },
            { caption: 'UrlLink', name: 'UrlLink', icon: 'link.png' },
            {
                caption: 'ColorPicker',
                name: 'ColorPicker',
                icon: 'colorpicker.png',
            },
            { caption: 'Label', name: 'Label', icon: 'label.png' },
        ];

        return copy;
    })();

    private createOption(caption, name, icon): TypeOption {
        return {
            caption: caption,
            name: name,
            iconUrl: this.toUrl(icon),
        };
    }

    private toUrl(url) {
        return ImageHandler.convertSource(
            '$$TEMPLATE_HANDLER$$?TemplateName=$$TEMPLATE_STYLE$$&ImageName=' +
                url,
            false
        );
    }

    private createCheckbox(
        label: LabelViewModelT,
        locator: ObservableOrDefault<string>
    ) {
        return new this.CheckBoxViewModel({
            label: label,
            isEnabled: false,
            locator: locator,
        });
    }

    private createExpander(labelErgName: string) {
        return new this.ExpanderBoxViewModel({
            link: null,
            labelErgName: labelErgName,
        });
    }

    private createLabel(ergName: string) {
        return this.LabelViewModel.create(ergName);
    }

    public dispose() {
        this.disposeSubscriptions();
        this.disposeMlsSubscriptions();
    }

    private disposeMlsSubscriptions() {
        if (!_.isEmpty(this.subContextSubscriptions)) {
            this.subContextSubscriptions.forEach(s => s.dispose());
        }
    }

    protected disposeSubscriptions() {
        if (!_.isEmpty(this.subscriptions)) {
            this.subscriptions.forEach(s => s.dispose());
        }
    }
}

const iconUrlTemplate =
    '# if (data.iconUrl) { #<img src=\"#:data.iconUrl#\"># } #<span>#:data.caption#</span>';

interface TypeOptionParameter {
    caption: string;
    name: string;
    icon: string;
}

interface TypeOption {
    caption: string;
    name: string;
    iconUrl: string;
}

export default EditMaskTabViewModel;
